// WbTicketCentrePaneLines — Phase C10 Group 3.
//
// Line-item editor for the New Ticket page. The local controlled state
// mirrors the server's `lines` array; on every change we PATCH the FULL
// replacement array up to the server via context.patchTicket (the context
// owns debounced autosave so we don't repeat the timer here).
//
// Price/VAT come from POST /wb/pricing/resolve — the server is canonical
// pricer. We invoke context.resolveLinePrice for any product/quantity/
// override change and let the context debounce + dispatch.
//
// The basis badge mirrors WbTicketDetailPage's basisBadge helper (kept
// inline so we don't grow a shared export surface in C10).

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import { Card } from '../../../../components/ui/Card/Card';
import { Button } from '../../../../components/ui/Button/Button';
import { Badge } from '../../../../components/ui/Badge/Badge';
import { Spinner } from '../../../../components/ui/Spinner/Spinner';
import { useWbTicket } from '../../context/WbTicketContext';
import { useWbLiveWeight } from '../../context/WbLiveWeightContext';
import { useWbProducts, type WbProduct } from '../../hooks/useWbProducts';
import { useWbProductGroups } from '../../hooks/useWbProductGroups';
import { WbProductCombobox } from './WbProductCombobox';
import type {
  TicketLineInput,
} from '../../context/WbTicketContext';
import type {
  TicketLineDto,
  TicketLineMode,
  UnitPriceBasis,
} from '../../hooks/useWbTicket';
import styles from './WbTicketCentrePane.module.css';

// Stable client-side line identity. Used to key resolvedPrices /
// resolvingPriceForLine so add/remove/reorder never mis-attaches a price to
// the wrong line (array-index keying did). A monotonic counter is enough —
// keys only need to be unique within one editing session.
let __lineUidCounter = 0;
function nextLineUid(): string {
  __lineUidCounter += 1;
  return `l${__lineUidCounter}`;
}

const GBP = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
});

const KG_FORMATTER = new Intl.NumberFormat('en-GB', {
  maximumFractionDigits: 1,
});

function fmtCurrency(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  return GBP.format(n);
}

const LINE_MODES: ReadonlyArray<{ value: TicketLineMode; label: string }> = [
  { value: 'weight', label: 'Weight' },
  { value: 'unit', label: 'Unit' },
  { value: 'custom', label: 'Custom' },
];

// -- Local editor row shape -------------------------------------------------
//
// We keep more state on the row than TicketLineInput holds (product object
// for cached attributes, an `override` boolean for the toggle, the raw
// inputs as strings so empty fields don't roundtrip as NaN). On every
// PATCH we project this back to TicketLineInput.

interface EditorLine {
  /** Stable client identity — keys resolvedPrices / resolvingPriceForLine. */
  uid: string;
  product_enc_id: string | null;
  product: WbProduct | null;
  /** Server snapshot of the product name, for displaying a selection whose
   * product isn't in the loaded catalogue. */
  product_name_snapshot: string | null;
  mode: TicketLineMode;
  quantity: string; // controlled string for the number input (unit/custom lines)
  gross: string; // controlled string — weight-mode lines only
  tare: string; // controlled string — weight-mode lines only
  unit_price: string;
  unit_price_basis: UnitPriceBasis | null;
  override: boolean;
  override_reason: string;
  ewc_code: string;
  is_custom: boolean;
  custom_description: string;
  /** Snapshot of VAT % so we can render even before pricing-resolve lands. */
  vat_rate_snapshot_pct: number | null;
}

function blankLine(): EditorLine {
  return {
    uid: nextLineUid(),
    product_enc_id: null,
    product: null,
    product_name_snapshot: null,
    mode: 'weight',
    quantity: '0',
    gross: '',
    tare: '',
    unit_price: '0',
    unit_price_basis: null,
    override: false,
    override_reason: '',
    ewc_code: '',
    is_custom: false,
    custom_description: '',
    vat_rate_snapshot_pct: null,
  };
}

function fromTicketLine(
  line: TicketLineDto,
  productsByEncId: Map<string, WbProduct>,
  productsByCode: Map<string, WbProduct>,
): EditorLine {
  // Entity enc_ids are re-encrypted with a random IV on every server response
  // (see server/shared/crypto.ts), so a hydrated line's product.enc_id is a
  // different ciphertext than the same product's enc_id in the /wb/products
  // catalogue — an enc_id-only match misses, leaving the product dropdown blank
  // and the UoM/pricing lookups (keyed on product_enc_id) unresolved. Resolve
  // the catalogue product by enc_id first, then by the stable product `code`,
  // and adopt the catalogue's enc_id so the <select> option matches.
  const code = line.product?.code ?? line.product_code_snapshot ?? null;
  const product =
    (line.product !== null ? productsByEncId.get(line.product.enc_id) : undefined) ??
    (code !== null ? productsByCode.get(code) : undefined) ??
    null;
  return {
    uid: nextLineUid(),
    product_enc_id: product?.enc_id ?? line.product?.enc_id ?? null,
    product,
    // Snapshot name so a product not in the loaded catalogue (since-deactivated
    // / beyond the page) can still be shown as the selected option.
    product_name_snapshot: line.product_name_snapshot ?? line.product?.name ?? null,
    mode: line.mode,
    quantity: String(line.quantity),
    gross: line.gross_weight_kg === null ? '' : String(line.gross_weight_kg),
    tare: line.tare_weight_kg === null ? '' : String(line.tare_weight_kg),
    unit_price: String(line.unit_price),
    unit_price_basis: line.unit_price_basis,
    override: line.unit_price_basis === 'manual_override',
    override_reason: line.override_reason ?? '',
    ewc_code: line.ewc_code_snapshot ?? '',
    is_custom: line.mode === 'custom',
    custom_description:
      line.mode === 'custom' ? line.product_name_snapshot ?? '' : '',
    vat_rate_snapshot_pct: line.vat_rate_snapshot_pct,
  };
}

/** Net weight in kg for a weight-mode line, or null when incomplete. */
function lineNetKg(line: EditorLine): number | null {
  if (line.mode !== 'weight') return null;
  const g = Number(line.gross);
  const t = Number(line.tare);
  if (line.gross.trim() === '' || line.tare.trim() === '') return null;
  if (!Number.isFinite(g) || !Number.isFinite(t)) return null;
  // Net is never negative — a tare that (transiently, mid-capture) exceeds the
  // gross clamps to 0 rather than producing a negative weight/quantity.
  return Math.max(0, Math.round((g - t) * 1000) / 1000);
}

/** Convert a kg net to the product's unit-of-measure (mirrors the server). */
function netInUom(
  netKg: number,
  uom: 'tonne' | 'kg' | 'each' | 'm3' | undefined,
): number {
  if (uom === 'tonne') return Math.round((netKg / 1000) * 1000) / 1000;
  return Math.round(netKg * 1000) / 1000;
}

function toTicketLineInput(
  line: EditorLine,
  uom?: 'tonne' | 'kg' | 'each' | 'm3',
): TicketLineInput {
  const price = Number(line.unit_price);
  // Weight-mode lines: the priced quantity is the net (gross - tare)
  // converted to the product's UoM; the server re-derives this authoritatively
  // from the gross/tare we send. `uom` comes from the authoritative catalogue
  // lookup (see lineUom) — falling back to line.product silently yields kg.
  const net = lineNetKg(line);
  const qty =
    line.mode === 'weight'
      ? net !== null
        ? netInUom(net, uom ?? line.product?.unit_of_measure)
        : 0
      : (() => {
          const n = Number(line.quantity);
          return Number.isFinite(n) ? n : 0;
        })();
  const out: TicketLineInput = {
    // Stable client identity — carried to the optimistic DTO so the pricing
    // summary keys resolvedPrices by it. Stripped before the wire send.
    uid: line.uid,
    mode: line.mode,
    quantity: qty,
    unit_price: Number.isFinite(price) ? price : 0,
  };
  if (line.mode === 'weight') {
    out.gross_weight_kg = line.gross.trim() === '' ? null : Number(line.gross);
    out.tare_weight_kg = line.tare.trim() === '' ? null : Number(line.tare);
    out.net_weight_kg = net;
  }
  if (line.product_enc_id !== null) out.product_enc_id = line.product_enc_id;
  if (line.unit_price_basis !== null) out.unit_price_basis = line.unit_price_basis;
  if (line.override && line.override_reason.trim() !== '') {
    out.override_reason = line.override_reason.trim();
  }
  // Only send an EWC code once it's a complete 6-digit value — the server
  // schema enforces /^\d{6}$/ and `.strict()`-rejects a partial code (400)
  // mid-typing. An omitted code is fine; the snapshot fills in on save.
  const ewc = line.ewc_code.trim();
  if (/^\d{6}$/.test(ewc)) out.ewc_code = ewc;
  if (line.is_custom) {
    out.is_custom = true;
    if (line.custom_description.trim() !== '') {
      out.custom_description = line.custom_description.trim();
    }
  }
  return out;
}

/**
 * Whether a projected line is safe to send. Mirrors the server's
 * `ticketLineInputSchema` refines so we never PATCH a line that 400s: a
 * non-custom line needs a product, a custom line needs a description. A
 * just-added blank line (no product yet) is held back until it's filled in.
 */
function isSendableLine(line: TicketLineInput): boolean {
  if (line.is_custom === true) {
    return (
      typeof line.custom_description === 'string' &&
      line.custom_description.length > 0
    );
  }
  return typeof line.product_enc_id === 'string';
}

function basisBadge(basis: UnitPriceBasis | null): JSX.Element {
  if (basis === null) return <span className={styles.muted}>—</span>;
  // price_band intentionally renders with the `info` (blue) variant —
  // the design system has no purple variant. Swap to purple when it
  // ships rather than introducing a one-off CSS colour for a single
  // badge (flag C8, deferred by design).
  if (basis === 'contract') return <Badge variant="info">Contract</Badge>;
  if (basis === 'price_band') return <Badge variant="info">Band</Badge>;
  if (basis === 'general_public') {
    return <Badge variant="muted">General public</Badge>;
  }
  return <Badge variant="warning">Manual override</Badge>;
}

export function WbTicketCentrePaneLines(): JSX.Element {
  const {
    ticket,
    patchTicket,
    resolveLinePrice,
    resolvedPrices,
    resolvingPriceForLine,
    clearResolvedPrice,
    activeLineUid,
    setActiveLineUid,
    setActiveLineWeights,
    registerActiveLineCapture,
    setHasWeightLines,
  } = useWbTicket();
  const { liveWeightKg } = useWbLiveWeight();

  // -- Products list ------------------------------------------------------
  const productsQ = useWbProducts({ is_active: true, per_page: 100 });
  const productGroups = useWbProductGroups().data?.groups ?? [];
  const products = productsQ.data?.products ?? [];
  const productsByEncId = useMemo(() => {
    const m = new Map<string, WbProduct>();
    for (const p of products) m.set(p.enc_id, p);
    return m;
  }, [products]);
  // Keyed by the stable product `code` so a hydrated line can be matched back
  // to its catalogue product even when its enc_id ciphertext has rotated.
  const productsByCode = useMemo(() => {
    const m = new Map<string, WbProduct>();
    for (const p of products) m.set(p.code, p);
    return m;
  }, [products]);
  // Stable boolean — flips false→true once the catalogue lands. Safe as an
  // effect dependency (unlike the `products` array, which gets a fresh `[]`
  // identity each render until the query resolves).
  const productsLoaded = productsQ.isSuccess;

  // -- Local editor lines -------------------------------------------------
  //
  // Re-sync from ticket whenever the ticket id changes (i.e. a hydrate or
  // a fresh create). Subsequent server echoes don't re-sync — we trust the
  // local edits and let the next PATCH replay them.
  const [lines, setLines] = useState<EditorLine[]>(() => {
    if (ticket === null) return [];
    return ticket.lines.map((l) => fromTicketLine(l, productsByEncId, productsByCode));
  });

  // Identity for the line resync. We deliberately key on the ticket enc_id,
  // which the context FREEZES across PATCH echoes (the server re-encrypts it
  // per response) — so this fires only when a genuinely different ticket is
  // created/hydrated, never on an echo (which would clobber local edits).
  const ticketEncId = ticket?.enc_id ?? null;
  useEffect(() => {
    if (ticket === null) {
      setLines([]);
      return;
    }
    setLines(ticket.lines.map((l) => fromTicketLine(l, productsByEncId, productsByCode)));
    // Intentionally only watch enc_id to avoid clobbering local typing on
    // every server echo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketEncId]);

  // Mirror `lines` into a ref so the mutation helpers can read the current
  // array without a stale closure — and, crucially, without performing their
  // side effects (pushPatch / triggerResolve) inside a setState updater. A
  // useState updater runs during the render phase (and twice under
  // StrictMode); calling context setters from there triggers React's
  // "Cannot update a component while rendering a different component" warning
  // and can fire duplicate PATCH/resolve calls.
  const linesRef = useRef(lines);
  useEffect(() => {
    linesRef.current = lines;
  }, [lines]);

  // -- Hydration: re-map + price-resolve once per ticket -----------------
  //
  // When an existing draft is loaded (`?from=enc_id`), the lines arrive via
  // hydrateFromTicket. The initial enc_id sync above may run before the
  // product catalogue has loaded, leaving each editor line without its
  // product object (uom / hazardous). Once products are in, re-map so the
  // lines carry their products, then kick a price resolve per line so the
  // per-line price / basis / total populate without the operator having to
  // touch each line. Guarded by a ref so it fires exactly once per ticket
  // identity (and never on a fresh create — that ticket has no lines yet).
  const hydratedKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (ticket === null || !productsLoaded) return;
    if (hydratedKeyRef.current === ticket.enc_id) return;
    hydratedKeyRef.current = ticket.enc_id;
    if (ticket.lines.length === 0) return;
    const mapped = ticket.lines.map((l) => fromTicketLine(l, productsByEncId, productsByCode));
    linesRef.current = mapped;
    setLines(mapped);
    mapped.forEach((l) => triggerResolve(l));
    // triggerResolve / productsByEncId are stable enough for this one-shot;
    // we deliberately key only on the ticket identity + catalogue readiness.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketEncId, productsLoaded]);

  // -- Active-line selection housekeeping --------------------------------
  //
  // Drop the selection if the active line was removed, and publish the
  // selected line's weight snapshot for the sibling weight pane to render.
  useEffect(() => {
    if (activeLineUid === null) {
      setActiveLineWeights(null);
      return;
    }
    const active = lines.find((l) => l.uid === activeLineUid);
    if (active === undefined) {
      setActiveLineUid(null);
      setActiveLineWeights(null);
      return;
    }
    const net = lineNetKg(active);
    setActiveLineWeights({
      gross_weight_kg: active.gross.trim() === '' ? null : Number(active.gross),
      tare_weight_kg: active.tare.trim() === '' ? null : Number(active.tare),
      net_weight_kg: net,
    });
  }, [activeLineUid, lines, setActiveLineUid, setActiveLineWeights]);

  // Publish whether any weight-mode line is present so the sibling weight pane
  // can choose between the per-line capture card and the legacy ticket-level
  // widget. Blank weight lines (no product yet) count — they're never sent in
  // the PATCH, so the persisted ticket.lines can't reveal them.
  useEffect(() => {
    setHasWeightLines(lines.some((l) => l.mode === 'weight'));
  }, [lines, setHasWeightLines]);

  // -- Mutation helpers --------------------------------------------------
  //
  // Patch the FULL local array up to the server (see context: lines are
  // sent as a replacement). Called from every input change handler.
  function pushPatch(nextLines: EditorLine[]) {
    if (ticket === null) return;
    // Each line carries its stable uid through to the optimistic DTO (see
    // toTicketLineInput / projectInputLineToDto) so the right-rail summary
    // keys resolvedPrices by uid directly — no positional bridge to drift.
    // uid is stripped before the request hits the wire. Incomplete lines
    // (a freshly-added blank row with no product yet) are held back so the
    // autosave PATCH never trips the server's line-validation refine (400);
    // they join the payload as soon as a product / description is picked.
    patchTicket({
      lines: nextLines
        .map((l) => toTicketLineInput(l, lineUom(l)))
        .filter(isSendableLine),
    });
  }

  // Authoritative unit-of-measure for a line. We look the product up in the
  // loaded catalogue by enc_id rather than trusting `line.product` to be
  // attached — a hydrated line (or one whose product loaded late) can carry a
  // product_enc_id with a null product object, and `netInUom(net, undefined)`
  // silently returns KILOGRAMS. Sending a kg quantity to the per-tonne pricer
  // yields a ×1000 subtotal, so the conversion must never guess.
  function lineUom(line: EditorLine): WbProduct['unit_of_measure'] | undefined {
    const fromCatalogue =
      line.product_enc_id !== null
        ? productsByEncId.get(line.product_enc_id)?.unit_of_measure
        : undefined;
    return fromCatalogue ?? line.product?.unit_of_measure;
  }

  function triggerResolve(line: EditorLine) {
    if (ticket === null) return;
    // Weight-mode lines price on their net (gross - tare) converted to the
    // product UoM; unit/custom lines price on the typed quantity.
    let qty: number;
    if (line.mode === 'weight') {
      const uom = lineUom(line);
      // Don't resolve a weight line until we know its UoM — otherwise we'd
      // send a kg quantity against a per-tonne price (×1000). The hydration
      // effect re-fires resolve once the catalogue lands.
      if (uom === undefined) return;
      const net = lineNetKg(line);
      qty = net === null ? 0 : netInUom(net, uom);
    } else {
      qty = Number(line.quantity);
    }
    if (!Number.isFinite(qty) || qty <= 0) return;
    if (line.is_custom) return; // custom lines bypass the catalogue pricer
    if (line.product_enc_id === null) return;
    const overridePrice = Number(line.unit_price);
    const useOverride =
      line.override &&
      Number.isFinite(overridePrice) &&
      overridePrice > 0 &&
      line.override_reason.trim() !== '';
    resolveLinePrice(line.uid, {
      customer_enc_id: ticket.customer.enc_id,
      product_enc_id: line.product_enc_id,
      quantity: qty,
      ...(useOverride
        ? {
            manual_override_price: overridePrice,
            manual_override_reason: line.override_reason.trim(),
          }
        : {}),
    });
  }

  function updateLine(idx: number, mutate: (l: EditorLine) => EditorLine) {
    const prev = linesRef.current;
    const current = prev[idx];
    if (current === undefined) return;
    const next = prev.slice();
    const updated = mutate(current);
    next[idx] = updated;
    // Keep the ref current synchronously so back-to-back calls in one tick
    // don't read a stale array (the syncing effect only runs after render).
    linesRef.current = next;
    setLines(next);
    // Side effects run OUTSIDE the state updater (event-handler phase), never
    // during render.
    pushPatch(next);
    triggerResolve(updated);
  }

  function handleProductChange(idx: number, encId: string | null) {
    const product = encId !== null ? productsByEncId.get(encId) ?? null : null;
    updateLine(idx, (l) => ({
      ...l,
      product_enc_id: encId,
      product,
      // Seed the line mode from the product's billing type (weight vs unit)
      // unless the operator has explicitly made this a custom line.
      mode: l.is_custom ? l.mode : product?.mode ?? l.mode,
    }));
  }

  function handleGrossChange(idx: number, e: ChangeEvent<HTMLInputElement>) {
    const v = e.target.value;
    updateLine(idx, (l) => ({ ...l, gross: v }));
  }

  function handleTareChange(idx: number, e: ChangeEvent<HTMLInputElement>) {
    const v = e.target.value;
    updateLine(idx, (l) => ({ ...l, tare: v }));
  }

  // Capture the live readout (SSE weight stream) into a line's gross or tare
  // cell. Uses the CURRENT reading (not gated on stability — the operator owns
  // the timing for the per-row buttons). Persists through the normal autosave
  // PATCH; net + priced quantity are derived client-side and re-priced server
  // side. Re-capturing overwrites the existing value.
  function handleCaptureInto(idx: number, column: 'gross' | 'tare') {
    if (liveWeightKg === null) return;
    const v = String(liveWeightKg);
    updateLine(idx, (l) => ({ ...l, [column]: v }));
  }

  // -- Active-line capture bridge (top button) --------------------------
  //
  // The sibling weight pane (WbTicketCentrePaneWeight) hosts the "Capture
  // Gross/Tare" button that targets the selected line. It can't reach this
  // editor's local line state, so we register an imperative handler on the
  // context. We keep activeLineUid in a ref so the (once-registered) handler
  // always resolves the CURRENT selection, and store the impl in a ref so it
  // closes over the latest updateLine without re-registering each render.
  const activeLineUidRef = useRef(activeLineUid);
  activeLineUidRef.current = activeLineUid;
  const captureImplRef = useRef<
    (type: 'gross' | 'tare', weightKg: number) => void
  >(() => {});
  captureImplRef.current = (type, weightKg) => {
    const uid = activeLineUidRef.current;
    if (uid === null) return;
    const cur = linesRef.current;
    const idx = cur.findIndex((l) => l.uid === uid);
    if (idx === -1) return;
    const v = String(weightKg);
    updateLine(idx, (l) => ({ ...l, [type]: v }));
    // After a tare the active line is fully weighed — advance the selection to
    // the next line still missing a gross (skipping the one just completed).
    if (type === 'tare') {
      const nextLine = cur.find(
        (l) => l.uid !== uid && l.mode === 'weight' && l.gross.trim() === '',
      );
      setActiveLineUid(nextLine?.uid ?? null);
    }
  };
  useEffect(() => {
    const stable = (type: 'gross' | 'tare', weightKg: number): void =>
      captureImplRef.current(type, weightKg);
    registerActiveLineCapture(stable);
    return () => registerActiveLineCapture(null);
  }, [registerActiveLineCapture]);

  function handleModeChange(idx: number, e: ChangeEvent<HTMLSelectElement>) {
    const next = e.target.value as TicketLineMode;
    updateLine(idx, (l) => ({
      ...l,
      mode: next,
      is_custom: next === 'custom',
      // Clear product when switching into custom (custom lines don't
      // resolve against the catalogue).
      product_enc_id: next === 'custom' ? null : l.product_enc_id,
      product: next === 'custom' ? null : l.product,
    }));
  }

  function handleQuantityChange(idx: number, e: ChangeEvent<HTMLInputElement>) {
    const v = e.target.value;
    updateLine(idx, (l) => ({ ...l, quantity: v }));
  }

  function handleUnitPriceChange(
    idx: number,
    e: ChangeEvent<HTMLInputElement>,
  ) {
    const v = e.target.value;
    updateLine(idx, (l) => ({ ...l, unit_price: v }));
  }

  function handleOverrideToggle(idx: number, e: ChangeEvent<HTMLInputElement>) {
    const checked = e.target.checked;
    updateLine(idx, (l) => ({
      ...l,
      override: checked,
      // When toggling off, clear the basis hint so the next resolve repopulates it.
      unit_price_basis: checked ? 'manual_override' : null,
    }));
  }

  function handleOverrideReasonChange(
    idx: number,
    e: ChangeEvent<HTMLInputElement>,
  ) {
    const v = e.target.value;
    updateLine(idx, (l) => ({ ...l, override_reason: v }));
  }

  function handleEwcChange(idx: number, e: ChangeEvent<HTMLInputElement>) {
    const v = e.target.value;
    updateLine(idx, (l) => ({ ...l, ewc_code: v }));
  }

  function handleCustomDescriptionChange(
    idx: number,
    e: ChangeEvent<HTMLInputElement>,
  ) {
    const v = e.target.value;
    updateLine(idx, (l) => ({ ...l, custom_description: v }));
  }

  function handleAddLine() {
    const next = [...linesRef.current, blankLine()];
    linesRef.current = next;
    setLines(next);
    pushPatch(next);
  }

  // Whole-row selection for per-line weight capture. Clicks that land on an
  // interactive control (product select, weight inputs, override label, the
  // Remove button, etc.) are ignored so editing never flips the selection;
  // clicking anywhere else on the row toggles it as the active line.
  function handleRowSelect(
    e: ReactMouseEvent<HTMLTableRowElement>,
    uid: string,
    isActive: boolean,
  ) {
    const target = e.target as HTMLElement;
    if (target.closest('input, select, button, textarea, label, a')) return;
    setActiveLineUid(isActive ? null : uid);
  }

  function handleRemove(idx: number) {
    const prev = linesRef.current;
    const removed = prev[idx];
    const next = prev.slice();
    next.splice(idx, 1);
    linesRef.current = next;
    setLines(next);
    pushPatch(next);
    // Drop the removed line's resolved-price entry so the map doesn't retain
    // keys for lines that no longer exist.
    if (removed !== undefined) clearResolvedPrice(removed.uid);
  }

  // -- Totals ------------------------------------------------------------
  //
  // Sum resolvedPrices (preferred) or the local line's projected subtotal.
  // Render "—" when ANY line is mid-resolve so the operator doesn't see a
  // half-stale total bouncing between values.
  const anyResolving = resolvingPriceForLine.size > 0;
  const totals = useMemo(() => {
    let subtotal = 0;
    let vat = 0;
    for (let i = 0; i < lines.length; i += 1) {
      const row = lines[i];
      if (row === undefined) continue;
      const resolved = resolvedPrices[row.uid];
      const rowNet = lineNetKg(row);
      const qty =
        row.mode === 'weight'
          ? rowNet === null
            ? 0
            : netInUom(rowNet, lineUom(row))
          : Number(row.quantity);
      const price = Number(row.unit_price);
      if (resolved !== undefined) {
        subtotal +=
          resolved.subtotal_ex_vat ??
          (Number.isFinite(qty) && Number.isFinite(price)
            ? qty * resolved.unit_price_ex_vat
            : 0);
        vat += resolved.vat_amount * (Number.isFinite(qty) ? qty : 0);
      } else if (Number.isFinite(qty) && Number.isFinite(price)) {
        subtotal += qty * price;
      }
    }
    return { subtotal, vat, total: subtotal + vat };
    // lineUom reads productsByEncId, so recompute when the catalogue lands.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines, resolvedPrices, productsByEncId]);

  // -- Render ------------------------------------------------------------
  if (ticket === null) {
    return (
      <Card title="Line items">
        <span className={styles.helperMuted}>
          Pick a customer to start adding lines.
        </span>
        <div className={styles.actionsBar} style={{ marginTop: '0.5rem' }}>
          <Button variant="secondary" disabled>
            Add line
          </Button>
        </div>
      </Card>
    );
  }

  // A locked ticket (completed / voided) freezes all weight capture.
  const ticketLocked =
    ticket.status === 'completed' || ticket.status === 'voided';

  return (
    <Card title="Line items" flush>
      <table className={styles.lineTable} data-testid="wb-lines-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Product</th>
            <th>Mode</th>
            <th className={styles.numCol}>Gross (kg)</th>
            <th className={styles.numCol}>Tare (kg)</th>
            <th className={styles.numCol}>Net / Qty</th>
            <th className={styles.numCol}>Unit price</th>
            <th>Basis</th>
            <th className={styles.numCol}>VAT %</th>
            <th className={styles.numCol}>Total</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {lines.length === 0 ? (
            <tr>
              <td colSpan={11}>
                <span className={styles.muted}>No lines yet.</span>
              </td>
            </tr>
          ) : (
            lines.map((line, idx) => {
              const resolved = resolvedPrices[line.uid];
              const isResolving = resolvingPriceForLine.has(line.uid);
              const basis = resolved?.basis ?? line.unit_price_basis;
              const vatPct =
                resolved?.vat_rate_snapshot_pct ?? line.vat_rate_snapshot_pct;
              const lineNet = lineNetKg(line);
              // Effective priced quantity: weight lines use net→UoM, others
              // the typed quantity.
              const qty =
                line.mode === 'weight'
                  ? lineNet === null
                    ? 0
                    : netInUom(lineNet, lineUom(line))
                  : Number(line.quantity);
              const unitEx = resolved?.unit_price_ex_vat ?? Number(line.unit_price);
              const subtotal = resolved?.subtotal_ex_vat ?? (
                Number.isFinite(qty) && Number.isFinite(unitEx) ? qty * unitEx : null
              );
              const vatAmt = resolved !== undefined && Number.isFinite(qty)
                ? resolved.vat_amount * qty
                : null;
              const rowTotal = subtotal !== null
                ? subtotal + (vatAmt ?? 0)
                : null;
              const overrideMissingReason =
                line.override && line.override_reason.trim() === '';
              const isHazardous = line.product?.is_hazardous === true;
              const isActive = line.uid === activeLineUid;
              return (
                <tr
                  key={idx}
                  className={`${styles.lineRowClickable} ${
                    isActive ? styles.lineRowActive : ''
                  }`}
                  onClick={(e) => handleRowSelect(e, line.uid, isActive)}
                  data-testid={`wb-line-row-${idx}`}
                  data-active={isActive ? 'true' : undefined}
                >
                  <td>
                    <span
                      className={styles.lineSelectMarker}
                      data-testid={`wb-line-select-${idx}`}
                      title="Click the row to select it for weight capture"
                      aria-hidden
                    >
                      {isActive ? '◉' : '○'}
                    </span>{' '}
                    {idx + 1}
                  </td>
                  <td>
                    {line.is_custom ? (
                      <input
                        type="text"
                        className={styles.lineInput}
                        value={line.custom_description}
                        onChange={(e) => handleCustomDescriptionChange(idx, e)}
                        placeholder="Custom description…"
                        aria-label={`Line ${idx + 1} custom description`}
                        data-testid={`wb-line-custom-desc-${idx}`}
                      />
                    ) : (
                      // A hydrated line's product may be absent from the loaded
                      // catalogue (deactivated, or beyond the page) — and its
                      // enc_id ciphertext won't match any option. selectedNameFallback
                      // shows the snapshot name so the selection never blanks.
                      <WbProductCombobox
                        products={products}
                        groups={productGroups}
                        value={line.product_enc_id}
                        onChange={(encId) => handleProductChange(idx, encId)}
                        selectedNameFallback={line.product_name_snapshot}
                        ariaLabel={`Line ${idx + 1} product`}
                        data-testid={`wb-line-product-${idx}`}
                      />
                    )}
                    {isHazardous ? (
                      <input
                        type="text"
                        className={styles.lineInput}
                        value={line.ewc_code}
                        onChange={(e) => handleEwcChange(idx, e)}
                        placeholder="EWC code"
                        aria-label={`Line ${idx + 1} EWC code`}
                        data-testid={`wb-line-ewc-${idx}`}
                        style={{ marginTop: '0.25rem' }}
                      />
                    ) : null}
                  </td>
                  <td>
                    <select
                      className={styles.lineSelect}
                      value={line.mode}
                      onChange={(e) => handleModeChange(idx, e)}
                      aria-label={`Line ${idx + 1} mode`}
                      data-testid={`wb-line-mode-${idx}`}
                    >
                      {LINE_MODES.map((m) => (
                        <option key={m.value} value={m.value}>
                          {m.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  {/* Gross (kg) — weight-mode lines only */}
                  <td className={styles.numCol}>
                    {line.mode === 'weight' ? (
                      <div className={styles.priceCell}>
                        <input
                          type="number"
                          className={`${styles.lineInput} ${styles.numCol}`}
                          value={line.gross}
                          onChange={(e) => handleGrossChange(idx, e)}
                          step={0.1}
                          min={0}
                          placeholder="0.0"
                          aria-label={`Line ${idx + 1} gross weight`}
                          data-testid={`wb-line-gross-${idx}`}
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCaptureInto(idx, 'gross')}
                          disabled={liveWeightKg === null || ticketLocked}
                          aria-label={`Capture gross into line ${idx + 1}`}
                          title="Capture live weight as gross"
                          data-testid={`wb-line-capture-gross-${idx}`}
                          style={{ minWidth: 36, minHeight: 36 }}
                        >
                          ⚖ G
                        </Button>
                      </div>
                    ) : (
                      <span className={styles.muted}>—</span>
                    )}
                  </td>
                  {/* Tare (kg) — weight-mode lines only */}
                  <td className={styles.numCol}>
                    {line.mode === 'weight' ? (
                      <div className={styles.priceCell}>
                        <input
                          type="number"
                          className={`${styles.lineInput} ${styles.numCol}`}
                          value={line.tare}
                          onChange={(e) => handleTareChange(idx, e)}
                          step={0.1}
                          min={0}
                          placeholder="0.0"
                          aria-label={`Line ${idx + 1} tare weight`}
                          data-testid={`wb-line-tare-${idx}`}
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCaptureInto(idx, 'tare')}
                          disabled={liveWeightKg === null || ticketLocked}
                          aria-label={`Capture tare into line ${idx + 1}`}
                          title="Capture live weight as tare"
                          data-testid={`wb-line-capture-tare-${idx}`}
                          style={{ minWidth: 36, minHeight: 36 }}
                        >
                          ⚖ T
                        </Button>
                      </div>
                    ) : (
                      <span className={styles.muted}>—</span>
                    )}
                  </td>
                  {/* Net (weight lines, read-only) / Qty (unit + custom) */}
                  <td className={styles.numCol}>
                    {line.mode === 'weight' ? (
                      <span
                        className={styles.weightValue}
                        data-testid={`wb-line-net-${idx}`}
                      >
                        {lineNet === null
                          ? '—'
                          : `${KG_FORMATTER.format(lineNet)} kg`}
                      </span>
                    ) : (
                      <input
                        type="number"
                        className={`${styles.lineInput} ${styles.numCol}`}
                        value={line.quantity}
                        onChange={(e) => handleQuantityChange(idx, e)}
                        step={1}
                        min={0}
                        aria-label={`Line ${idx + 1} quantity`}
                        data-testid={`wb-line-qty-${idx}`}
                      />
                    )}
                  </td>
                  <td className={styles.numCol}>
                    <div className={styles.priceCell}>
                      <input
                        type="number"
                        className={`${styles.lineInput} ${styles.numCol} ${
                          line.override ? '' : styles.lineInputReadOnly
                        }`}
                        value={
                          line.override
                            ? line.unit_price
                            : resolved?.unit_price_ex_vat !== undefined
                              ? String(resolved.unit_price_ex_vat)
                              : line.unit_price
                        }
                        onChange={(e) => handleUnitPriceChange(idx, e)}
                        readOnly={!line.override}
                        step={0.01}
                        min={0}
                        aria-label={`Line ${idx + 1} unit price`}
                        data-testid={`wb-line-price-${idx}`}
                      />
                      {isResolving ? <Spinner size="sm" label="Resolving price" /> : null}
                    </div>
                    <label className={styles.overrideRow}>
                      <input
                        type="checkbox"
                        checked={line.override}
                        onChange={(e) => handleOverrideToggle(idx, e)}
                        aria-label={`Line ${idx + 1} override price`}
                        data-testid={`wb-line-override-${idx}`}
                      />
                      <span>Override</span>
                    </label>
                    {line.override ? (
                      <>
                        <input
                          type="text"
                          className={styles.lineInput}
                          value={line.override_reason}
                          onChange={(e) => handleOverrideReasonChange(idx, e)}
                          placeholder="Reason for override"
                          aria-label={`Line ${idx + 1} override reason`}
                          data-testid={`wb-line-override-reason-${idx}`}
                          style={{ marginTop: '0.25rem' }}
                        />
                        {overrideMissingReason ? (
                          <span className={styles.errorText}>
                            Reason is required when overriding.
                          </span>
                        ) : null}
                      </>
                    ) : null}
                  </td>
                  <td data-testid={`wb-line-basis-${idx}`}>{basisBadge(basis)}</td>
                  <td className={styles.numCol}>
                    {vatPct === null || vatPct === undefined
                      ? '—'
                      : `${vatPct.toFixed(1)}%`}
                  </td>
                  <td
                    className={styles.numCol}
                    data-testid={`wb-line-total-${idx}`}
                  >
                    {rowTotal === null ? '—' : fmtCurrency(rowTotal)}
                  </td>
                  <td>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemove(idx)}
                      aria-label={`Remove line ${idx + 1}`}
                      data-testid={`wb-line-remove-${idx}`}
                    >
                      Remove
                    </Button>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>

      <div className={styles.actionsBar} style={{ padding: '0.5rem' }}>
        <Button
          variant="secondary"
          onClick={handleAddLine}
          data-testid="wb-lines-add"
        >
          Add line
        </Button>
      </div>

      <div className={styles.totalsRow} style={{ padding: '0 0.5rem 0.5rem' }}>
        <div className={styles.totalsCell}>
          <span className={styles.totalsLabel}>Subtotal</span>
          <span className={styles.totalsValue}>
            {anyResolving ? '—' : fmtCurrency(totals.subtotal)}
          </span>
        </div>
        <div className={styles.totalsCell}>
          <span className={styles.totalsLabel}>VAT</span>
          <span className={styles.totalsValue}>
            {anyResolving ? '—' : fmtCurrency(totals.vat)}
          </span>
        </div>
        <div className={styles.totalsCell}>
          <span className={styles.totalsLabel}>Total</span>
          <span className={`${styles.totalsValue} ${styles.totalsGrand}`}>
            {anyResolving ? '—' : fmtCurrency(totals.total)}
          </span>
        </div>
      </div>
    </Card>
  );
}
